import React, { useCallback, useEffect } from 'react'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import Text from 'core/elements/Text'
import { detachVolume } from './actions'
import useUpdateAction from 'core/hooks/useUpdateAction'
import ModalForm from 'core/elements/modal/ModalForm'
import ServiceUnhealthyInfo from 'openstack/components/common/ServiceUnhealthyInfo'
import ApiClient from 'api-client/ApiClient'

const useStyles = makeStyles<Theme>((theme) => ({
  fields: {
    gap: 16,
    display: 'grid',
  },
}))

const { nova } = ApiClient.getInstance()

export default function DetachVolumeDialog({ rows: [volume], onClose, reloadVolumes }) {
  const classes = useStyles()
  const { update, updating, error, reset } = useUpdateAction(detachVolume)
  const [vmDetails, setVmDetails] = React.useState(null)
  const [loading, setLoading] = React.useState(true)

  // Fetch VM details
  useEffect(() => {
    const getVmDetails = async () => {
      setLoading(true)
      const vm = await nova.getInstance(volume.attachedVm?.id)
      setVmDetails(vm)
      setLoading(false)
    }

    getVmDetails()
  }, [volume])

  const handleSubmit = useCallback(async () => {
    reset()
    const { error } = await update({ volumeId: volume.id, vmId: volume.attachedVm?.id })
    if (!error) {
      reloadVolumes?.()
      handleClose(true)
    }
  }, [volume, update, reset, reloadVolumes])

  const handleClose = (success?: boolean) => {
    reset()
    onClose(success)
  }

  return (
    <ModalForm
      title="Detach Volume"
      onSubmit={handleSubmit}
      onClose={onClose}
      submitting={updating}
      error={error}
      submitTitle={`Detach Volume`}
      panel="dialog"
      maxWidth={800}
      loading={loading}
      open
    >
      <ServiceUnhealthyInfo action="Volume Detachment" hypervisor blockStorage />
      <div className={classes.fields}>
        <Text variant="body2">
          You are about to detach the volume <b>{volume.name || volume.id}</b> from the VM{' '}
          <b>{volume.attachedVm?.name || volume.attachedVm?.id}</b>.
        </Text>
        {vmDetails?.['OS-EXT-STS:vm_state'] === 'active' && (
          <Text variant="body2">
            <b>WARNING</b>: The virtual machine this volume is attached to is currently active. To
            avoid risks, make sure that the guest operating system is not making any active changes
            to this volume before detaching it.
          </Text>
        )}
      </div>
    </ModalForm>
  )
}
